<input #attachmentFileInput multiple hidden type="file" [attr.accept]="acceptedFileTypes" (change)="onAttachmentsSelected($event)" />
@if (pendingAttachments.length > 0) {
  <div class="chat-input-attachments">
    @for (attachment of pendingAttachments; track attachment; let i = $index) {
      <div class="chat-input-attachment-chip">
        <img class="chat-input-attachment-icon" [src]="getAttachmentIcon(attachment)" alt="" />
        <div class="chat-input-attachment-info">
          <span class="chat-input-attachment-name text-truncate" [title]="attachment.name">{{ attachment.name }}</span>
          <span class="chat-input-attachment-size">{{ getAttachmentSize(attachment) }}</span>
        </div>
        <button
          type="button"
          class="chat-input-attachment-remove"
          [attr.aria-label]="TranslationKeys.GeneralRemove | translate"
          (click)="removeAttachment(i)"
        >
          <i [nimbusIcon]="ICONS.TIMES_CIRCLE" [iconWeight]="IconWeight.Light"></i>
        </button>
      </div>
    }
  </div>
}
@if ((voiceRecorder.state$ | async) !== VoiceRecordingState.Preview) {
  <div class="chat-input-message" [class.expanded]="isExpanded">
    @if (messageLimitExceededBy < 0) {
      <span class="chat-input-negative-counter chat-input-negative-counter-root">
        {{ messageLimitExceededBy }}
        <nimbus-tooltip [text]="TranslationKeys.ChatWidgetMessageLimitExceededTooltip | translate" position="left"></nimbus-tooltip>
      </span>
    }
    @if (isParked) {
      <i class="chat-input-lock" [nimbusIcon]="ICONS.LOCK" [iconWeight]="IconWeight.Light"></i>
    }
    <div class="chat-input-content">
      @if (isExpanded) {
        <div class="chat-format-toolbar">
          @for (formatButton of formatToolbarButtons; track formatButton.type) {
            <button
              type="button"
              class="chat-format-toolbar-btn"
              [class.active]="activeFormats.has(formatButton.type)"
              [disabled]="disabled || isParked"
              [attr.aria-label]="formatButton.labelKey | translate"
              [attr.aria-pressed]="activeFormats.has(formatButton.type)"
              (click)="applyFormatting(formatButton.type)"
            >
              <i [nimbusIcon]="formatButton.icon"></i>
              <nimbus-tooltip position="above" [text]="formatButton.labelKey | translate"></nimbus-tooltip>
            </button>
          }
        </div>
      }
      @if ((voiceRecorder.state$ | async) === VoiceRecordingState.Idle) {
        <textarea
          #messageTextarea
          class="chat-input-textarea"
          [class.disabled]="disabled"
          [class.locked]="isParked"
          [class.expanded]="isExpanded"
          rows="1"
          [formControl]="messageControl"
          [placeholder]="inputPlaceholderTKey | translate"
          (input)="resizeTextarea(); updateActiveFormats()"
          (keydown)="onKeyDown($event)"
          (keyup.arrowleft)="updateActiveFormats()"
          (keyup.arrowright)="updateActiveFormats()"
          (keyup.arrowup)="updateActiveFormats()"
          (keyup.arrowdown)="updateActiveFormats()"
          (keyup.home)="updateActiveFormats()"
          (keyup.end)="updateActiveFormats()"
          (click)="updateActiveFormats()"
          (paste)="onPaste($event)"
        ></textarea>
      } @else {
        <div class="chat-recording-indicator">
          <span class="chat-recording-dot"></span>
          <span class="chat-recording-time">{{ recordingTime$ | async }}</span>
        </div>
      }
    </div>
    <div class="chat-input-actions">
      @if ((voiceRecorder.state$ | async) === VoiceRecordingState.Idle) {
        @if (isMediaHandlingEnabled) {
          @if (isVoiceRecordingSupported) {
            <span class="chat-input-mic-container chat-input-action-tooltip-container">
              <button
                type="button"
                class="chat-input-mic"
                [attr.aria-label]="TranslationKeys.ChatWidgetVoiceMessageTooltip | translate"
                [disabled]="isRecordingDisabled"
                (click)="startRecording()"
              >
                <i [nimbusIcon]="(voiceRecorder.error$ | async) ? ICONS.MICROPHONE_SLASH : ICONS.MICROPHONE"></i>
              </button>
              @if (pendingAttachments.length > 0) {
                <nimbus-tooltip
                  [text]="TranslationKeys.ChatWidgetRecordingWithAttachmentsTooltip | translate"
                  position="left"
                ></nimbus-tooltip>
              } @else {
                <nimbus-tooltip position="above" [text]="TranslationKeys.ChatWidgetVoiceMessageTooltip | translate"></nimbus-tooltip>
              }
            </span>
          }
          @if (isAttachmentSupported) {
            <span class="chat-input-action-tooltip-container">
              <button
                type="button"
                class="chat-input-attach"
                [attr.aria-label]="TranslationKeys.ChatWidgetAttachFilesTooltip | translate"
                [disabled]="disabled"
                (click)="triggerAttachmentPicker()"
              >
                <i [nimbusIcon]="ICONS.ATTACHMENT"></i>
              </button>
              <nimbus-tooltip position="above" [text]="TranslationKeys.ChatWidgetAttachFilesTooltip | translate"></nimbus-tooltip>
            </span>
          }
        }

        @if (isFormattingSupported) {
          <button
            type="button"
            class="chat-format-toggle"
            [class.active]="showFormattingOptions"
            [disabled]="disabled || isParked"
            [attr.aria-label]="TranslationKeys.ChatWidgetFormattingOptionsToggle | translate"
            [attr.aria-pressed]="showFormattingOptions"
            [attr.aria-expanded]="showFormattingOptions"
            (click)="toggleFormattingOptions()"
          >
            <img class="chat-format-toggle-icon" [src]="chatFormatToggleIconPath" alt="" />
            <nimbus-tooltip position="above" [text]="TranslationKeys.ChatWidgetFormattingOptionsToggle | translate"></nimbus-tooltip>
          </button>
        }
        @if (isCustomerChat) {
          <button
            #emojiPickerToggle
            type="button"
            class="chat-input-emoji-toggle"
            [disabled]="disabled || isParked"
            [attr.aria-label]="TranslationKeys.ChatWidgetEmojiPickerOpen | translate"
            [attr.aria-expanded]="showEmojiPicker"
            (click)="toggleEmojiPicker()"
          >
            <i [nimbusIcon]="ICONS.SMILE_LIGHT" [iconWeight]="IconWeight.Light"></i>
            <nimbus-tooltip position="above" [text]="TranslationKeys.ChatWidgetEmojiPickerOpen | translate"></nimbus-tooltip>
          </button>
          <span class="chat-input-actions-divider" aria-hidden="true"></span>
          @if (showEmojiPicker && !disabled && !isParked) {
            <div #emojiPickerContainer class="chat-input-emoji-picker-container"></div>
          }
        }

        <span class="chat-input-action-tooltip-container">
          <button
            type="button"
            class="chat-input-arrow"
            [attr.aria-label]="TranslationKeys.ChatWidgetSendMessageTooltip | translate"
            [class.enabled]="canSend"
            [class.disabled]="!canSend"
            [disabled]="!canSend"
            (click)="sendMessage()"
          >
            <i [nimbusIcon]="ICONS.ARROW_RIGHT"></i>
          </button>
          <nimbus-tooltip position="above" [text]="TranslationKeys.ChatWidgetSendMessageTooltip | translate"></nimbus-tooltip>
        </span>
      } @else {
        <button type="button" class="chat-input-stop" (click)="stopRecording()">
          <i [nimbusIcon]="ICONS.STOP"></i>
        </button>
      }
    </div>
  </div>
} @else {
  <div class="chat-input-preview">
    <nimbus-input-media-player class="chat-input-preview-player" [source]="voiceRecorder.blob$ | async"></nimbus-input-media-player>
    <div class="chat-input-preview-actions">
      <button type="button" class="chat-input-discard" (click)="discardRecording()">
        <i [nimbusIcon]="ICONS.TRASH_LIGHT"></i>
      </button>
      <span class="chat-input-action-tooltip-container">
        <button
          type="button"
          class="chat-input-arrow"
          [attr.aria-label]="TranslationKeys.ChatWidgetSendMessageTooltip | translate"
          [class.enabled]="!disabled"
          [class.disabled]="disabled"
          [disabled]="disabled"
          (click)="sendVoiceMessage()"
        >
          <i [nimbusIcon]="ICONS.ARROW_RIGHT"></i>
        </button>
        <nimbus-tooltip position="above" [text]="TranslationKeys.ChatWidgetSendMessageTooltip | translate"></nimbus-tooltip>
      </span>
    </div>
  </div>
}
@if (showCounter) {
  <div class="chat-input-message-counter">
    <i [nimbusIcon]="ICONS.PEN_LIGHT"></i>
    <span>{{ smsCharsRemaining }}</span>
    <i [nimbusIcon]="ICONS.COMMENT_ALT_LIGHT" [iconWeight]="IconWeight.Light"></i>
    <span>{{ smsSegmentsCount }}</span>
  </div>
}
